import {
    DisplayableMissionDto,
    ErrorContainer,
    FieldError,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionActions,
    MissionService,
    ResultDto
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    filter,
    map,
    startWith,
    switchMap,
    takeUntil
} from 'rxjs/operators';

export const FINALISATION_BLOCKED_DEFAULT_REASON =
    'The mission has unresolved requirements and cannot be finalised at this stage';

export const INCOMPLETE_ITEMS_REASON =
    'Not all required items have been completed';

/**
 * API errors for things the mission forms validate as they're edited. The
 * saved result is out of date for these as soon as the form changes, so the
 * form's validity is used instead.
 */
export const FORM_VALIDATED_ERRORS = [
    /^outcome$/,
    /^sorties\[\d+\]\.(pilot|craft)$/
];

/**
 * Only an approver can finalise conformance results, so they don't stop the
 * mission being submitted to them.
 */
const IGNORED_ON_SUBMIT = [/^conformance$/];

export type FinalisationAction = 'submit' | 'finalise';

type FinalisationError = [key: string, error: FieldError];

interface FinalisationCheckNotRequired {
    type: 'not_required';
}
interface FinalisationCheckChecking {
    type: 'checking';
}
interface FinalisationCheckChecked {
    type: 'checked';
    errors: FinalisationError[];
}
interface FinalisationCheckFailed {
    type: 'failed';
    error: FlyFreelyError;
}

export type FinalisationCheck =
    | FinalisationCheckNotRequired
    | FinalisationCheckChecking
    | FinalisationCheckChecked
    | FinalisationCheckFailed;

/**
 * The saved mission isn't in a state to be finalised, so we need to ask the
 * API why.
 */
export function needsFinalisationCheck(actions: MissionActions) {
    return actions != null && !actions.canRequestFinalisation;
}

/**
 * The API's errors in the order they're reported: state errors, which is
 * where finalisation problems are reported, then field errors.
 */
function toFinalisationErrors(
    result: ResultDto<ErrorContainer>
): FinalisationError[] {
    const errors = [
        ...Object.entries(result.value?.stateErrors ?? {}),
        ...Object.entries(result.value?.fieldErrors ?? {})
    ];
    if (errors.length === 0 && !result.success) {
        return [['result', { message: result.message }]];
    }
    return errors;
}

function matchesAny(patterns: RegExp[], key: string) {
    return patterns.some(pattern => pattern.test(key));
}

function withoutIgnored(
    errors: FinalisationError[],
    action: FinalisationAction
) {
    return action === 'submit'
        ? errors.filter(([key]) => !matchesAny(IGNORED_ON_SUBMIT, key))
        : errors;
}

/**
 * The message of the first error, a generic reason if it has no message, or
 * null if there are no errors.
 */
function firstReason(errors: FinalisationError[]) {
    if (errors.length === 0) {
        return null;
    }
    return errors[0][1]?.message ?? FINALISATION_BLOCKED_DEFAULT_REASON;
}

export function incompleteItemsReason(items: string[]) {
    return items.length > 0
        ? `${INCOMPLETE_ITEMS_REASON}: ${items.join(', ')}`
        : INCOMPLETE_ITEMS_REASON;
}

/**
 * @param force ask the API even if the mission says it can be finalised
 */
export function checkFinalisation(
    missionService: MissionService,
    mission: DisplayableMissionDto,
    force = false
): Observable<FinalisationCheck> {
    if (!force && !needsFinalisationCheck(mission.availableActions)) {
        return of({ type: 'not_required' });
    }
    return missionService.checkCanFinaliseMission(mission.id).pipe(
        map(
            (result): FinalisationCheck => ({
                type: 'checked',
                errors: toFinalisationErrors(result)
            })
        ),
        catchError((error: FlyFreelyError) =>
            of<FinalisationCheck>({ type: 'failed', error })
        ),
        startWith<FinalisationCheck>({ type: 'checking' })
    );
}

/**
 * Tracks whether the saved mission can be submitted for finalisation or
 * finalised. Call `refresh` whenever the mission is saved.
 */
export class FinalisationChecker {
    private refreshSource = new Subject<{
        mission: DisplayableMissionDto;
        force: boolean;
    }>();
    private stateSubject = new BehaviorSubject<FinalisationCheck>({
        type: 'not_required'
    });
    /**
     * Set when the check before an action stops it, until the next refresh.
     */
    private actionBlocked: {
        action: FinalisationAction;
        reason: string;
    } | null = null;

    state$ = this.stateSubject.asObservable();

    /**
     * @param formValidatedErrors API errors the dialogue already validates as
     * it's edited, which are ignored when deciding whether to block an action
     */
    constructor(
        missionService: MissionService,
        private logging: FlyFreelyLoggingService,
        private formValidatedErrors: RegExp[],
        destroy$: Observable<void>
    ) {
        this.refreshSource
            .pipe(
                switchMap(({ mission, force }) =>
                    checkFinalisation(missionService, mission, force)
                ),
                takeUntil(destroy$)
            )
            .subscribe(check => {
                if (check.type === 'failed') {
                    logging.error(
                        check.error,
                        `Error checking if the mission can be finalised: ${check.error.message}`
                    );
                }
                this.actionBlocked = null;
                this.stateSubject.next(check);
            });
    }

    /**
     * @param force ask the API even if the mission says it can be finalised,
     * for when the mission may be out of date
     */
    refresh(mission: DisplayableMissionDto, force = false) {
        this.refreshSource.next({ mission, force });
    }

    /**
     * A check is in progress, so whether actions are blocked isn't known yet.
     */
    get checking() {
        return this.stateSubject.value.type === 'checking';
    }

    /**
     * Why the action is blocked by something that can't be fixed by editing
     * the form, or null if it isn't.
     */
    blockedReason(action: FinalisationAction) {
        if (this.actionBlocked?.action === action) {
            return this.actionBlocked.reason;
        }
        const check = this.stateSubject.value;
        if (check.type !== 'checked') {
            return null;
        }
        return firstReason(
            withoutIgnored(check.errors, action).filter(
                ([key]) => !matchesAny(this.formValidatedErrors, key)
            )
        );
    }

    /**
     * Waits for the check of the mission that was just saved. Resolves if the
     * action can go ahead. Otherwise shows why and rejects without an error, so
     * the calling action stops.
     */
    confirmCanProceed(action: FinalisationAction): Promise<void> {
        return firstValueFrom(
            this.state$.pipe(filter(check => check.type !== 'checking'))
        ).then(check => {
            if (check.type === 'failed') {
                // The error has already been shown
                return Promise.reject();
            }
            const reason =
                check.type === 'checked'
                    ? firstReason(withoutIgnored(check.errors, action))
                    : null;
            if (reason == null) {
                return Promise.resolve();
            }
            this.logging.error(null, reason);
            this.actionBlocked = { action, reason };
            // Let subscribers show the reason
            this.stateSubject.next(check);
            return Promise.reject();
        });
    }
}
